import React from 'react';
import { Reveal } from './Reveal';
import './About.scss';

interface Experience {
  date: string;
  title: string;
  company: string;
  description: React.ReactNode;
}

const L: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    className="exp-row__link"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-cursor-hover
  >
    {children}
  </a>
);

const experiences: Experience[] = [
  {
    date: '2023 – Present',
    title: 'Advanced Technologist',
    company: 'The Boeing Company',
    description: (
      <>
        Advanced Technologist in Digital Product Development, Boeing Commercial Airplanes.
        Learn more about <L href="https://cascade.boeing.com/">Cascade</L>, Boeing's Climate
        Impact Model, in charting a sustainable aerospace together.
      </>
    ),
  },
  {
    date: '2020 – 2023',
    title: 'Consultant',
    company: 'Capgemini',
    description:
      'Supported clients across the Asia Pacific region, focusing on strategic and operational improvements in financial services, manufacturing, automotive, and sustainability.',
  },
  {
    date: '2017 – 2022',
    title: 'Co-Founder',
    company: 'Pitchspot',
    description: (
      <>
        <L href="https://pitchspot.co/">Pitchspot</L> is an innovation management platform for
        anyone to embark on their entrepreneurship journey, discover and share new ideas, and
        adopt new methodologies on innovation and productivity. Products include Launchpad,
        Academy, Zone, and Labs.
      </>
    ),
  },
  {
    date: '2019',
    title: 'Founder-in-Residence',
    company: 'Antler Singapore',
    description: (
      <>
        Antler SG3 Cohort with <L href="https://pitchspot.co/">Pitchspot</L>.
      </>
    ),
  },
  {
    date: '2017 – 2019',
    title: 'Assistant Instructor',
    company: 'NUS Enterprise',
    description:
      'Co-instructor / teaching assistant for Agile Market Validation in the Lean Launchpad Programme, and Seminars in Entrepreneurship: Lean Methodology.',
  },
  {
    date: '2026 – Present',
    title: 'Doctor of Business Administration (DBA)',
    company: 'Durham University Business School',
    description:
      'DBA candidate at Durham University Business School, a Triple Crown accredited school (AACSB, AMBA and EQUIS). My research interests sit at the intersection of digital strategy and business model innovation, informed by my experience in digital product development and venture building.',
  },
  {
    date: '2025 – Present',
    title: 'Master of Business Administration (MBA)',
    company: 'Nanyang Technological University, Nanyang Business School',
    description: (
      <>
        Pursuing the Nanyang MBA to sharpen my edge in strategy, leadership, and innovation –
        bridging deep technology with real business impact.{' '}
        <L href="https://www.tiktok.com/@ryanchongzx/video/7535121179128909063">
          Catch the story on TikTok
        </L>
        .
      </>
    ),
  },
  {
    date: '2014 – 2018',
    title: 'BBA (Honours), Finance',
    company: 'National University of Singapore, School of Business',
    description: (
      <>
        NOC Overseas Colleges Programme · Startup Weekend Singapore Mega (Third Prize) · NUS
        Enterprise Summer Programme Organising Committee · Residential College 4 Resident
        Assistant & Founding Team, Coffee Academy.
      </>
    ),
  },
];

const About: React.FC = () => {
  return (
    <section id="experience" className="exp">
      <div className="exp__inner section-inner">
        <Reveal className="section-head">
          <h2 className="section-head__label">Experience</h2>
          <p className="section-head__statement">
          Roles, ventures, studies: each a fresh expression of one raison d'être.
          </p>
        </Reveal>

        <div className="exp__list">
          {experiences.map((exp, idx) => (
            <Reveal key={idx} className="exp-row" hoverable>
              <div className="exp-row__dates">{exp.date}</div>
              <div className="exp-row__head">
                <div className="exp-row__role">{exp.title}</div>
                <div className="exp-row__company">{exp.company}</div>
              </div>
              <div className="exp-row__desc">{exp.description}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
